import React, { useState } from 'react';
import clsx from 'clsx';
import { CalendarIcon } from '@heroicons/react/24/outline';
import type { ObjectiveCardProps } from './objectiveCard.types';
import { getLevelConfig, formatLastUpdate } from './objectiveCard.definitions';
import { ProgressBar } from '../ProgressBar';
import { useCyclesQuery } from '../../hooks/data/useCyclesQuery';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared/CollaboratorAvatar';

export default function ObjectiveCard({
  objective,
  showKeyResults = false,
  showCycleBadge = true,
  onExpand,
  onClick,
  className,
}: ObjectiveCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const levelConfig = getLevelConfig(objective.level);
  const { cycles, isLoading: cyclesLoading } = useCyclesQuery();

  // Find the cycle for this objective
  const cycle = cycles?.find((c) => c.id === objective.cycleId);

  // Debug: Log to understand the issue
  if (!cycle && !cyclesLoading && cycles) {
    console.log('[ObjectiveCard] Cycle not found:', {
      objectiveCycleId: objective.cycleId,
      availableCycles: cycles.map(c => ({ id: c.id, name: c.name })),
      objectiveTitle: objective.title
    });
  }

  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
    onExpand?.(objective.id);
  };

  const handleClick = () => {
    onClick?.(objective.id);
  };

  return (
    <div
      className={clsx(
        'bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200',
        onClick && 'cursor-pointer',
        className
      )}
      onClick={handleClick}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span
                className={clsx(
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border font-montserrat',
                  levelConfig.bgColor,
                  levelConfig.textColor,
                  levelConfig.borderColor
                )}
              >
                Nível {objective.level} - {levelConfig.label}
              </span>
              {/* Cycle Badge - only show if showCycleBadge is true */}
              {showCycleBadge && (
                <>
                  {cyclesLoading ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border border-gray-300 bg-gray-50 text-gray-600 font-montserrat">
                      <CalendarIcon className="h-3 w-3" />
                      Carregando...
                    </span>
                  ) : cycle ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border border-blue-300 bg-blue-50 text-blue-700 font-montserrat">
                      <CalendarIcon className="h-3 w-3" />
                      Ciclo: {cycle.name}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border border-red-300 bg-red-50 text-red-700 font-montserrat">
                      <CalendarIcon className="h-3 w-3" />
                      Ciclo não encontrado
                    </span>
                  )}
                </>
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 font-montserrat mb-1">
              {objective.title}
            </h3>
            {objective.description && (
              <p className="text-sm text-gray-600 font-montserrat mb-1">
                {objective.description}
              </p>
            )}
            {/* Last Update Date */}
            <p className="text-xs text-gray-500 font-montserrat mb-2">
              Atualizado: {formatLastUpdate(objective.updatedAt)}
            </p>
            {/* Responsibles Avatars */}
            {objective.responsibles && objective.responsibles.length > 0 && (
              <div className="flex items-center gap-1">
                {objective.responsibles.map((responsible) => (
                  <CollaboratorAvatar
                    key={responsible.id}
                    photoUrl={responsible.photoUrl}
                    name={responsible.name}
                    size="xsmall"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Section */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 font-montserrat">
            Progresso Geral
          </span>
          <span className="text-sm font-semibold text-gray-900 font-montserrat">
            {objective.progress}%
          </span>
        </div>
        <ProgressBar
          progress={objective.progress}
          size="md"
          showPercentage={false}
          animated={true}
        />
      </div>

      {/* Key Results Toggle */}
      {showKeyResults && objective.keyResultCount > 0 && (
        <div className="px-4 pb-4">
          <button
            onClick={handleExpand}
            className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-150 font-montserrat"
          >
            <span>
              {objective.keyResultCount}{' '}
              {objective.keyResultCount === 1
                ? 'Resultado-Chave'
                : 'Resultados-Chave'}
            </span>
            <svg
              className={clsx(
                'w-5 h-5 transition-transform duration-200',
                isExpanded && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isExpanded && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 font-montserrat italic">
                Resultados-Chave serão exibidos aqui (integração futura)
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
